import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useRecentActions } from '../contexts/RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { respondToQuestion } from '../services/patient/hold';
import { fetchRecordsPatientHeader } from '../services/patient';
import { buildRecordsPatient, type RecordsPatient } from './shared/recordsPatient';

/**
 * Pull a human-readable message out of an API error. The backend error
 * envelope is `{ success: false, error: { code, message } }`, so we read
 * `error.message`; we also tolerate a plain-string `error` (legacy) and a
 * top-level `message`. Avoids rendering an error object as "[object Object]".
 */
function extractErrorDetail(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data as
            { error?: { message?: string } | string; message?: string } | undefined;
        const errorField = data?.error;
        if (typeof errorField === 'string') return errorField;
        if (errorField?.message) return errorField.message;
        if (data?.message) return data.message;
        return err.message;
    }
    return err instanceof Error ? err.message : 'Unknown error';
}

/**
 * Manages the Respond-to-question modal. Opening it pre-fetches the patient
 * header fields; the modal's `<ClarificationHistory />` fetches the
 * back-and-forth thread on its own (TanStack Query, key `['clarifications', id]`).
 * Submitting calls ExitHold via /workflow/hold/respond-to-question so the
 * engine resumes the case for the admin to re-pick up.
 */
export function useRespondToQuestionWorkflow() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [isOpen, setIsOpen] = useState(false);
    const [patient, setPatient] = useState<RecordsPatient | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const open = useCallback(
        async (patientId: string) => {
            setIsOpen(true);
            setPatient(null);
            try {
                const data = await fetchRecordsPatientHeader(patientId);
                setPatient(buildRecordsPatient(patientId, data));
            } catch {
                showToast('Failed to load patient info.', 'error');
                setIsOpen(false);
            }
        },
        [showToast]
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setPatient(null);
    }, []);

    const submit = useCallback(
        async (response: string) => {
            if (!patient?.id) return;
            const trimmed = response.trim();
            if (trimmed.length === 0) {
                showToast('Please enter a response before submitting.', 'error');
                return;
            }
            setIsSubmitting(true);
            try {
                await respondToQuestion(patient.id, trimmed);
                queryClient.invalidateQueries({ queryKey: ['patients'] });
                queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
                // Refresh the shared clarifications cache so the next viewer (or
                // the modal itself, on re-open) sees the new response.
                queryClient.invalidateQueries({ queryKey: ['clarifications', patient.id] });
                markActionCompleted(patient.id, 'Respond to question');
                showToast('Response sent to ODO.', 'success');
                close();
            } catch (err) {
                // 409 = the engine has already moved on (e.g. ODO/admin progressed
                // the case in another tab between open and submit). The response
                // itself isn't lost in a meaningful sense — the case just isn't
                // waiting for it anymore. Surface that distinctly, refresh the
                // patient view so the action ribbon re-syncs, and close.
                const is409 = axios.isAxiosError(err) && err.response?.status === 409;
                if (is409) {
                    queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
                    queryClient.invalidateQueries({ queryKey: ['patients'] });
                    queryClient.invalidateQueries({ queryKey: ['clarifications', patient.id] });
                    // 409 = already resolved elsewhere: the required action is gone,
                    // so record it as completed for sticky-sort purposes even though
                    // this branch also shows an error toast (this call didn't fail,
                    // it was just too late).
                    markActionCompleted(patient.id, 'Respond to question');
                    showToast('This case has already been handled. Refreshing your view.', 'error');
                    close();
                    return;
                }
                const detail = extractErrorDetail(err);
                showToast(`Failed to send response: ${detail}`, 'error');
            } finally {
                setIsSubmitting(false);
            }
        },
        [patient, queryClient, showToast, close, markActionCompleted]
    );

    return {
        isOpen,
        patient,
        isSubmitting,
        open,
        close,
        submit
    };
}
